import { z } from 'zod';
import { pillarAccessSchema, pillarSchema, userRoleSchema, userStatusSchema } from '../../common/schemas/enum.schemas.js';
import { uuidSchema } from '../../common/schemas/id.schemas.js';
/** One stored grant. Only an OWNER has rows; ADMIN/PM edit and MANAGER views every pillar by role (plan §4). */
export const pillarGrantSchema = z.object({
    pillar: pillarSchema,
    access: pillarAccessSchema,
});
/** At most one grant per pillar. */
export const pillarGrantsSchema = z
    .array(pillarGrantSchema)
    .max(5)
    .refine((grants) => new Set(grants.map((grant) => grant.pillar)).size === grants.length, 'Each pillar can be granted once');
export const userSchema = z.object({
    id: uuidSchema,
    email: z.string().email(),
    name: z.string(),
    role: userRoleSchema,
    status: userStatusSchema,
    mustChangePassword: z.boolean(),
    lastLoginAt: z.string().datetime().nullable(),
    /** Set while the per-user login lock is running. */
    lockedUntil: z.string().datetime().nullable(),
    /** Empty for every role except OWNER. */
    pillarAccess: z.array(pillarGrantSchema),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
export const listUsersInputSchema = z.object({
    search: z.string().trim().max(120).optional(),
    role: userRoleSchema.optional(),
    status: userStatusSchema.optional(),
});
export const listUsersOutputSchema = z.object({
    items: z.array(userSchema),
});
export const getUserInputSchema = z.object({ id: uuidSchema });
const userNameSchema = z.string().trim().min(1, 'Name is required').max(255, 'Use at most 255 characters');
const userEmailSchema = z.string().trim().min(1, 'Email is required').email('Enter a valid email address').max(255);
export const createUserInputSchema = z.object({
    email: userEmailSchema,
    name: userNameSchema,
    role: userRoleSchema,
    status: userStatusSchema.default('ACTIVE'),
    /** Stored for an OWNER only; ignored for the other roles, whose access follows from the role. */
    pillarAccess: pillarGrantsSchema.default([]),
});
export const updateUserInputSchema = z.object({
    id: uuidSchema,
    email: userEmailSchema.optional(),
    name: userNameSchema.optional(),
    role: userRoleSchema.optional(),
    status: userStatusSchema.optional(),
    /** Replaces every grant when present (OWNER only; cleared for other roles). */
    pillarAccess: pillarGrantsSchema.optional(),
});
/** Create and reset both answer with the one-time temporary password; it is never retrievable again. */
export const userWithTemporaryPasswordSchema = z.object({
    user: userSchema,
    temporaryPassword: z.string().min(1),
});
export const resetUserPasswordInputSchema = z.object({
    id: uuidSchema,
    /** When true (the default) the user must choose their own password at the next sign-in. */
    requireChange: z.boolean().default(true),
});
export const revokeUserSessionsInputSchema = z.object({ id: uuidSchema });
export const revokeUserSessionsOutputSchema = z.object({
    /** How many live refresh tokens were revoked. */
    revoked: z.number().int().min(0),
});
//# sourceMappingURL=users.schemas.js.map